import { FC, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { Autocomplete, Box, Chip, FormControlLabel, Grid, Switch, TextField } from '@mui/material'

import { useGetTrackedArchetypesQuery } from '../../api/markets'
import { MarketArchetype } from '../../api/markets'
import { getBlueprintDisplayName, groupMarketLevelBlueprints, sortMarketLevelBlueprints } from './marketLevelBlueprintGrouping'

type MarketLevelBlueprintOption = MarketArchetype

interface MarketLevelBlueprintSearchProps {
  value: string | undefined // MarketArchetype id
  onChange: (value: string | undefined) => void
  label?: string
  disabled?: boolean
  showDiscontinuedToggle?: boolean
}

export const MarketLevelBlueprintSearch: FC<MarketLevelBlueprintSearchProps> = ({
  value,
  onChange,
  label,
  disabled = false,
  showDiscontinuedToggle = true,
}) => {
  const { t } = useTranslation()
  const { data: trackedArchetypes, isLoading } = useGetTrackedArchetypesQuery()

  const [showDiscontinued, setShowDiscontinued] = useState(false)

  // Get all blueprints
  const allBlueprints: MarketLevelBlueprintOption[] = useMemo(() => {
    return trackedArchetypes || []
  }, [trackedArchetypes])

  // Group blueprints by the live event type their active events use
  const { groupings, isGrouped } = useMemo(
    () => groupMarketLevelBlueprints(allBlueprints, { uncategorizedLabel: t('internal-live-events:blueprint_group_uncategorized') }),
    [allBlueprints, t]
  )

  // Filter, then order by group and alphabetically by name within each group
  const filteredBlueprints = useMemo(() => {
    const visibleBlueprints = allBlueprints.filter((blueprint) => showDiscontinued || !blueprint.discontinued)

    return sortMarketLevelBlueprints(visibleBlueprints, groupings, isGrouped)
  }, [allBlueprints, showDiscontinued, groupings, isGrouped])

  // Get the selected blueprint object
  const selectedBlueprint = useMemo(() => {
    if (!value) return null
    return allBlueprints.find((bp) => bp.id === value) || null
  }, [value, allBlueprints])

  // Format display label with blueprint name
  const getOptionLabel = (option: MarketLevelBlueprintOption | string): string => {
    if (typeof option === 'string') {
      const blueprint = allBlueprints.find((bp) => bp.id === option)
      if (blueprint) {
        return getBlueprintDisplayName(blueprint)
      }
      return option
    }
    return getBlueprintDisplayName(option)
  }

  // Custom filter function for Autocomplete (filters by name in all languages, and by event type)
  const filterOptions = (options: MarketLevelBlueprintOption[], { inputValue }: { inputValue: string }) => {
    if (!inputValue) return options

    const searchLower = inputValue.toLowerCase()
    return options.filter((blueprint) => {
      const nameEn = blueprint.names.en?.toLowerCase() || ''
      const nameJa = blueprint.names.ja?.toLowerCase() || ''
      const nameZh = blueprint.names.zh?.toLowerCase() || ''
      const eventTypes = groupings.get(blueprint.id)?.searchText || ''
      return nameEn.includes(searchLower) || nameJa.includes(searchLower) || nameZh.includes(searchLower) || eventTypes.includes(searchLower)
    })
  }

  const getOptionGroup = (option: MarketLevelBlueprintOption): string => groupings.get(option.id)?.label || ''

  const handleAutocompleteChange = (_event: unknown, newValue: MarketLevelBlueprintOption | null) => {
    onChange(newValue?.id || undefined)
  }

  const handleDiscontinuedToggle = (event: React.ChangeEvent<HTMLInputElement>) => {
    setShowDiscontinued(event.target.checked)
  }

  return (
    <Grid container spacing={2}>
      {/* Autocomplete Search */}
      <Grid item xs={9}>
        <Box>
          <Autocomplete
            value={selectedBlueprint}
            onChange={handleAutocompleteChange}
            options={filteredBlueprints}
            getOptionLabel={getOptionLabel}
            groupBy={isGrouped ? getOptionGroup : undefined}
            filterOptions={filterOptions}
            loading={isLoading}
            disabled={disabled}
            fullWidth
            size="small"
            renderInput={(params) => <TextField {...params} placeholder={t('internal-live-events:search_blueprint_placeholder')} />}
            renderOption={(props, option) => (
              <li {...props} key={option.id}>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <Box component="span" sx={{ fontWeight: 'bold' }}>
                    {getBlueprintDisplayName(option)}
                  </Box>
                  {option.discontinued && <Chip label={t('live-events:live_event_evolution_type_discontinued')} size="small" color="error" sx={{ ml: 1 }} />}
                </Box>
              </li>
            )}
            isOptionEqualToValue={(option, value) => option.id === value.id}
          />
        </Box>
      </Grid>

      {/* Discontinued Toggle */}
      <Grid item xs={3} sx={{ display: 'flex', alignItems: 'center' }}>
        {showDiscontinuedToggle && (
          <FormControlLabel
            control={<Switch checked={showDiscontinued} onChange={handleDiscontinuedToggle} size="small" />}
            label={t('internal-live-events:show_discontinued_blueprints')}
          />
        )}
      </Grid>
    </Grid>
  )
}
